import { createTheme, darken } from '@mui/material/styles';

const FALLBACK_HEADING = 'Geist';
const FALLBACK_BODY = 'Geist';

// NORI light surfaces: White page, pure-white cards, Deep Black ink.
const lightSurface = {
  background: { default: '#F5F5F5', paper: '#FFFFFF' },
  text: { primary: '#0F0F10', secondary: 'rgba(15,15,16,0.62)' },
  divider: 'rgba(15,15,16,0.14)',
  action: {
    hover: 'rgba(15,15,16,0.05)',
    selected: 'rgba(15,15,16,0.09)',
    disabled: 'rgba(15,15,16,0.30)',
    disabledBackground: 'rgba(15,15,16,0.10)',
  },
};

// NORI dark surfaces: Deep Black page, Charcoal cards, Muted Gray secondary text.
const darkSurface = {
  background: { default: '#0F0F10', paper: '#171717' },
  text: { primary: '#F5F5F5', secondary: '#B7B7B7' },
  divider: 'rgba(245,245,245,0.12)',
  action: {
    hover: 'rgba(245,245,245,0.07)',
    selected: 'rgba(245,245,245,0.13)',
    disabled: 'rgba(245,245,245,0.35)',
    disabledBackground: 'rgba(245,245,245,0.12)',
  },
};

const barberSurfaceLight = {
  background: { default: '#F6F1E8', paper: '#FFFBF4' },
  text: { primary: '#2B1F12', secondary: 'rgba(43,31,18,0.70)' },
  divider: 'rgba(43,31,18,0.14)',
  action: {
    hover: 'rgba(43,31,18,0.05)',
    selected: 'rgba(43,31,18,0.09)',
    disabled: 'rgba(43,31,18,0.32)',
    disabledBackground: 'rgba(43,31,18,0.10)',
  },
};

const spaSurfaceLight = {
  background: { default: '#F4F0EA', paper: '#FBF7F1' },
  text: { primary: '#2E3A2F', secondary: 'rgba(46,58,47,0.68)' },
  divider: 'rgba(46,58,47,0.14)',
  action: {
    hover: 'rgba(46,58,47,0.05)',
    selected: 'rgba(46,58,47,0.09)',
    disabled: 'rgba(46,58,47,0.30)',
    disabledBackground: 'rgba(46,58,47,0.10)',
  },
};

const surfaceFor = (scheme, mode) => {
  if (mode !== 'light') return darkSurface;
  if (scheme === 'barber') return barberSurfaceLight;
  if (scheme === 'spa') return spaSurfaceLight;
  return lightSurface;
};

const SCHEME_STYLE = {
  // NORI house style: bold-but-quiet Geist headings, no forced uppercase,
  // pill buttons per the brand guide.
  gym: {
    radius: 12,
    buttonRadius: 999,
    headingTransform: 'none',
    headingTracking: '-0.01em',
    headingWeight: 700,
    h3Weight: 700,
    bodyTracking: 'normal',
    captionTransform: 'none',
    captionTracking: '0.04em',
    buttonTransform: 'none',
    buttonTracking: '0.01em',
    buttonWeight: 600,
  },
  barber: {
    radius: 6,
    headingTransform: 'none',
    headingTracking: '0.005em',
    headingWeight: 700,
    h3Weight: 700,
    bodyTracking: 'normal',
    captionTransform: 'uppercase',
    captionTracking: '0.18em',
    buttonTransform: 'none',
    buttonTracking: '0.02em',
    buttonWeight: 600,
  },
  spa: {
    radius: 14,
    headingTransform: 'none',
    headingTracking: '0.01em',
    headingWeight: 500,
    h3Weight: 500,
    bodyTracking: '0.01em',
    captionTransform: 'none',
    captionTracking: '0.06em',
    buttonTransform: 'none',
    buttonTracking: '0.04em',
    buttonWeight: 600,
  },
};

const quote = (name) => {
  if (!name) return null;
  return /\s/.test(name) ? `"${name}"` : name;
};

const buildFontStack = (primary, override) => {
  const head = override || primary || FALLBACK_BODY;
  const parts = [quote(head)];
  if (override && primary && override !== primary) parts.push(quote(primary));
  parts.push('"Geist"', '"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif');
  return parts.filter(Boolean).join(', ');
};

export const createBrandingTheme = (branding = {}, mode = 'dark') => {
  const b = branding || {};
  const scheme = b.scheme || 'gym';
  const style = SCHEME_STYLE[scheme] || SCHEME_STYLE.gym;
  const isLight = mode === 'light';
  const surface = surfaceFor(scheme, mode);

  const primaryMain = isLight
    ? b.primary_color_light || b.primary_color || '#171717'
    : b.primary_color || '#CEFB52';
  const primaryContrast = isLight
    ? b.primary_contrast_light || b.primary_contrast || '#CEFB52'
    : b.primary_contrast || '#0F0F10';
  const secondaryMain = isLight
    ? b.secondary_color_light || b.secondary_color || '#1F7CFF'
    : b.secondary_color || '#1F7CFF';
  const errorMain = isLight
    ? b.error_color_light || b.error_color || '#E04848'
    : b.error_color || '#FF5C5C';
  const warningMain = isLight
    ? b.warning_color_light || b.warning_color || '#D6219C'
    : b.warning_color || '#FF2FB8';

  // Filled buttons always wear the brand accent (the dark-mode primary), even
  // in light mode where palette.primary doubles as a readable heading color.
  const accentMain = b.primary_color || '#CEFB52';
  const accentContrast = b.primary_contrast || '#0F0F10';

  const bodyFamily = buildFontStack(b.font_body || FALLBACK_BODY, b.font_family);
  const headingFamily = buildFontStack(b.font_heading || FALLBACK_HEADING, b.font_family);
  const headingBase = {
    fontFamily: headingFamily,
    textTransform: style.headingTransform,
    letterSpacing: style.headingTracking,
  };

  const radius = b.border_radius ?? style.radius;

  return createTheme({
    scheme,
    palette: {
      mode,
      primary: { main: primaryMain, contrastText: primaryContrast },
      secondary: { main: secondaryMain },
      error: { main: errorMain },
      warning: { main: warningMain },
      // Product-level accents (not tenant-configurable): NORI Accent Blue for
      // informational states, Success Green for completed states.
      info: { main: '#1F7CFF' },
      success: { main: isLight ? '#5C9A1F' : '#9BD72C' },
      ...surface,
    },
    typography: {
      fontFamily: bodyFamily,
      body1: { letterSpacing: style.bodyTracking },
      body2: { letterSpacing: style.bodyTracking },
      h1: { ...headingBase, fontWeight: style.headingWeight },
      h2: { ...headingBase, fontWeight: style.headingWeight },
      h3: { ...headingBase, fontWeight: style.h3Weight },
      h4: { ...headingBase, fontWeight: style.h3Weight },
      h5: { ...headingBase, fontWeight: style.h3Weight },
      h6: { ...headingBase, fontWeight: style.h3Weight },
      subtitle1: { fontFamily: headingFamily, letterSpacing: style.headingTracking },
      subtitle2: { fontFamily: headingFamily, letterSpacing: style.headingTracking },
      caption: {
        textTransform: style.captionTransform,
        letterSpacing: style.captionTracking,
      },
      overline: {
        textTransform: style.captionTransform,
        letterSpacing: style.captionTracking,
      },
      button: {
        fontWeight: style.buttonWeight,
        letterSpacing: style.buttonTracking,
        textTransform: style.buttonTransform,
      },
    },
    shape: { borderRadius: radius },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: style.buttonRadius ?? radius,
            textTransform: style.buttonTransform,
            fontWeight: style.buttonWeight,
            letterSpacing: style.buttonTracking,
          },
          ...(isLight && {
            containedPrimary: {
              backgroundColor: accentMain,
              color: accentContrast,
              '&:hover': { backgroundColor: darken(accentMain, 0.12) },
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius,
            transition: theme.transitions.create(['box-shadow', 'transform'], {
              duration: theme.transitions.duration.shortest,
            }),
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.secondary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            },
          }),
          input: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            '&.Mui-focused': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ theme }) => ({ color: theme.palette.text.secondary }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({ borderColor: theme.palette.divider }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: Math.max(radius, 8) },
        },
      },
    },
  });
};

export default createBrandingTheme;
